import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface Interaction {
  id: number | string;
  name: string;
  [key: string]: any;
}

export interface InteractionConfigUpdate {
  delfos_server_config_id?: string;
  delfos_llm_config_id?: string;
  di2winocr_server_config_id?: string;
  di2winocr_features_config_id?: string;
}

export interface ExecuteOptions {
  delfos_server_config_id?: string | null;
  delfos_llm_config_id?: string | null;
  di2winocr_server_config_id?: string | null;
  di2winocr_features_config_id?: string | null;
  di2winocr_features_override?: Record<string, any> | null;
  clear_delfos_cache: boolean;
  clear_di2winocr_cache: boolean;
  use_multimodal: boolean;
  ai_model_language: 'pt' | 'en' | 'es';
  references_mode?: 'output_only' | 'input_output';
  archive_ids?: string[] | null;
}

export interface PaginatedResponse<T> {
  interactions: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class InteractionsService {
  private baseUrl = '/api/interactions/';

  constructor(private http: HttpClient) {}

  list(tag?: string, search?: string, page: number = 1, pageSize: number = 10, simulationType?: string): Observable<PaginatedResponse<Interaction>> {
    let url = `${this.baseUrl}?page=${page}&page_size=${pageSize}`;
    if (tag) {
      url += `&tag=${encodeURIComponent(tag)}`;
    }
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    if (simulationType) {
      url += `&simulation_type=${encodeURIComponent(simulationType)}`;
    }
    return this.http.get<PaginatedResponse<Interaction>>(url);
  }

  getById(interactionId: number | string): Observable<any> {
    return this.http.get<{ message: string; interaction: any }>(`${this.baseUrl}${interactionId}`)
      .pipe(map(res => res.interaction));
  }

  create(name: string, simulationType: string = 'extraction'): Observable<Interaction> {
    return this.http.post<Interaction>(this.baseUrl, { name, simulation_type: simulationType });
  }

  delete(interactionId: number | string): Observable<any> {
    return this.http.delete(`${this.baseUrl}${interactionId}`);
  }

  listArchives(interactionId: number | string): Observable<any[]> {
    return this.http.get<{message: string, archives: any[]}>(`${this.baseUrl}${interactionId}/archives`)
      .pipe(
        map(response => response.archives || [])
      );
  }

  updateArchives(interactionId: number | string, archivesIds: string[]): Observable<any> {
    return this.http.put(`${this.baseUrl}${interactionId}/archives`, { archives_ids: archivesIds });
  }

  updateConfigurations(interactionId: number | string, configurations: InteractionConfigUpdate): Observable<any> {
    return this.http.put(`${this.baseUrl}${interactionId}/configurations`, configurations);
  }

  updateModel(interactionId: number | string, modelId: string): Observable<any> {
    return this.http.put(`${this.baseUrl}${interactionId}/model`, {
      model_id: modelId,
    });
  }

  rename(interactionId: number | string, name: string): Observable<any> {
    return this.http.put(`${this.baseUrl}${interactionId}`, { name });
  }

  execute(interactionId: number | string, options: ExecuteOptions): Observable<any> {
    return this.http.post(`${this.baseUrl}${interactionId}/results`, options);
  }
}